import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { Icons as I } from '../pack-icons';
import BrandMark from './BrandMark';
import { useAuth } from '../../contexts/AuthContext';

const NAV = [
  { id: 'dashboard', label: 'Dashboard', Icon: I.home, to: '/app' },
  { id: 'create-post', label: 'Create Post', Icon: I.sparkles, to: '/app/create' },
  { id: 'calendar', label: 'Calendar', Icon: I.calendar, to: '/app/calendar' },
  { id: 'library', label: 'Library', Icon: I.library, to: '/app/library' },
  { id: 'plan', label: 'Content Plan', Icon: I.target, to: '/app/plan' },
];

/**
 * Map a route pathname to the active sidebar nav id.
 * Exported so the Topbar / AppShell can mirror current section if needed.
 */
export function navIdForPath(pathname) {
  if (pathname === '/app' || pathname === '/app/') return 'dashboard';
  if (pathname.startsWith('/app/create')) return 'create-post';
  if (pathname.startsWith('/app/calendar')) return 'calendar';
  if (pathname.startsWith('/app/library')) return 'library';
  if (pathname.startsWith('/app/plan')) return 'plan';
  return null;
}

export default function Sidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const current = navIdForPath(pathname);
  const { user, logout } = useAuth?.() || {};
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (!menuRef.current?.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const handleSignOut = async () => {
    setMenuOpen(false);
    try {
      await logout?.();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const go = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <aside className="sidebar">
      <div className="sb-top">
        <Link to="/app" className="sb-brand">
          <BrandMark size={28} withText={true} />
        </Link>
      </div>

      <Link to="/app/create" className="sb-cta">
        <I.plus size={14} />
        <span>New post</span>
        <span className="kbd" style={{ marginLeft: 'auto' }}>N</span>
      </Link>

      <nav className="sb-nav">
        {NAV.map((item) => (
          <NavLink
            key={item.id}
            to={item.to}
            end={item.to === '/app'}
            className={({ isActive }) => `sb-link ${isActive || current === item.id ? 'is-active' : ''}`}
          >
            <item.Icon size={16} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sb-sep" />

      <NavLink to="/app/referrals" className={({ isActive }) => `sb-link sb-link-sm ${isActive ? 'is-active' : ''}`}>
        <I.share size={16} />
        <span>Referrals</span>
      </NavLink>
      <NavLink to="/app/settings" className={({ isActive }) => `sb-link sb-link-sm ${isActive ? 'is-active' : ''}`}>
        <I.settings size={16} />
        <span>Settings</span>
      </NavLink>
      <NavLink to="/app/help" className={({ isActive }) => `sb-link sb-link-sm ${isActive ? 'is-active' : ''}`}>
        <I.info size={16} />
        <span>Help Center</span>
      </NavLink>

      <div className="sb-foot" ref={menuRef}>
        <button
          type="button"
          className={`sb-user ${menuOpen ? 'is-open' : ''}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <div className="sb-user-av">{(user?.name || user?.email || 'U').slice(0, 1).toUpperCase()}</div>
          <div className="sb-user-text">
            <div className="sb-user-name">{user?.name || user?.email || 'Account'}</div>
            <div className="sb-user-sub">{user?.plan || 'Free'}</div>
          </div>
          <I.chevUp size={14} style={{ color: 'var(--text-subtle)' }} />
        </button>
        {menuOpen && (
          <div className="sb-user-menu" role="menu">
            <div className="sb-user-menu-h">
              <div className="sb-user-menu-name">{user?.name || 'Account'}</div>
              {user?.email && <div className="sb-user-menu-email">{user.email}</div>}
            </div>
            <button type="button" className="sb-user-menu-item" onClick={() => go('/app/settings?tab=profile')}>
              <I.user size={14} /> <span>Profile</span>
            </button>
            <button type="button" className="sb-user-menu-item" onClick={() => go('/app/settings?tab=billing')}>
              <I.zap size={14} /> <span>Plan &amp; billing</span>
            </button>
            <button type="button" className="sb-user-menu-item" onClick={() => go('/app/settings?tab=accounts')}>
              <I.share size={14} /> <span>Connected accounts</span>
            </button>
            <button type="button" className="sb-user-menu-item" onClick={() => go('/app/help')}>
              <I.info size={14} /> <span>Help center</span>
            </button>
            <div className="sb-user-menu-sep" />
            <button type="button" className="sb-user-menu-item sb-user-menu-danger" onClick={handleSignOut}>
              <I.logout size={14} /> <span>Sign out</span>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
